/**
 * Project Store — project items, channels, active items
 */
import { createStore } from 'zustand/vanilla';
import type { ProjectItem, Channel } from '@graphicsim/shared-types';
import { eventBus } from '../engine/eventBus';

let nextItemNum = 1;

export interface ProjectState {
  items: ProjectItem[];
  channels: Channel[];
  activeChannelIndex: number;
  // Actions
  addItem: (item: Omit<ProjectItem, 'id' | 'channelIndex'>) => ProjectItem;
  resetProject: () => void;
  removeItem: (itemId: string) => void;
  removeItems: (itemIds: string[]) => void;
  updateField: (itemId: string, fieldId: string, value: string) => void;
  updateItem: (itemId: string, updates: Partial<ProjectItem>) => void;
  getItem: (itemId: string) => ProjectItem | undefined;
  getChannelItems: () => ProjectItem[];
  setActiveChannel: (index: number) => void;
  addChannel: (name?: string) => void;
  removeChannel: (index: number) => void;
  renameChannel: (index: number, name: string) => void;
}

export const projectStore = createStore<ProjectState>((set, get) => ({
  items: [],
  channels: [{ index: 0, name: 'Channel 1' }],
  activeChannelIndex: 0,

  resetProject: () => {
    set({ items: [], channels: [{ index: 0, name: 'Channel 1' }], activeChannelIndex: 0 });
    nextItemNum = 1;
  },

  addItem: (partial) => {
    const item: ProjectItem = {
      scaleX: 100,
      scaleY: 100,
      fitMode: 'fit' as const,
      ...partial,
      id: `proj-${Date.now()}-${nextItemNum++}`,
      channelIndex: get().activeChannelIndex,
    };
    set({ items: [...get().items, item] });
    eventBus.emit('project:itemAdded', { item });
    return item;
  },

  removeItem: (itemId) => {
    set({ items: get().items.filter((i) => i.id !== itemId) });
    eventBus.emit('project:itemRemoved', { itemId });
  },

  removeItems: (itemIds) => {
    const idSet = new Set(itemIds);
    set({ items: get().items.filter((i) => !idSet.has(i.id)) });
    itemIds.forEach((itemId) => eventBus.emit('project:itemRemoved', { itemId }));
  },

  updateField: (itemId, fieldId, value) => {
    set({
      items: get().items.map((i) =>
        i.id === itemId ? { ...i, fieldValues: { ...i.fieldValues, [fieldId]: value } } : i
      ),
    });
    eventBus.emit('project:itemUpdated', { itemId, fieldId, value });
  },

  updateItem: (itemId, updates) => {
    set({
      items: get().items.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
    });
  },

  getItem: (itemId) => get().items.find((i) => i.id === itemId),

  getChannelItems: () => {
    const ch = get().activeChannelIndex;
    return get().items.filter((i) => i.channelIndex === ch);
  },

  setActiveChannel: (index) => {
    set({ activeChannelIndex: index });
    eventBus.emit('project:channelChanged', { channelIndex: index });
  },

  addChannel: (name) => {
    const channels = get().channels;
    const newIndex = channels.length > 0 ? Math.max(...channels.map((c) => c.index)) + 1 : 0;
    const channel: Channel = { index: newIndex, name: name || `Channel ${newIndex + 1}` };
    set({ channels: [...channels, channel] });
    eventBus.emit('project:channelAdded', { channel });
  },

  removeChannel: (index) => {
    const channels = get().channels.filter((c) => c.index !== index);
    if (channels.length === 0) return; // keep at least one
    set({
      channels,
      items: get().items.filter((i) => i.channelIndex !== index),
      activeChannelIndex: channels[0].index,
    });
    eventBus.emit('project:channelRemoved', { channelIndex: index });
  },

  renameChannel: (index, name) => {
    set({
      channels: get().channels.map((c) => (c.index === index ? { ...c, name } : c)),
    });
  },
}));
